import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { BrandMark } from "@/components/BrandLogo";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const [params] = useSearchParams();
  const resetToken = params.get("token");
  const [mode, setMode] = useState(resetToken ? "reset" : (params.get("mode") === "register" ? "register" : "signin"));
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signin") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        login(data);
        nav("/app");
      } else if (mode === "register") {
        const { data } = await api.post("/auth/register", form);
        login(data);
        nav("/app");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If that email exists, a reset link has been sent.");
        setMode("signin");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: resetToken, password: form.password });
        toast.success("Password updated. Please sign in.");
        setMode("signin");
        nav("/login");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    signin: "Welcome back",
    register: "Create your account",
    forgot: "Reset your password",
    reset: "Set a new password",
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-black px-4">
      <div className="pointer-events-none absolute inset-0 diamond-radial" />
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-gold/[0.04] blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <BrandMark size={72} glow />
          <h1 className="mt-5 font-heading text-3xl font-bold tracking-tight">
            Diamond<span className="text-gradient-gold"> AI</span>
          </h1>
          <p className="mt-2 text-xs uppercase tracking-[0.35em] text-gold/60">Think. Create. Build. Execute.</p>
        </div>

        <div className="relative rounded-2xl border border-white/10 bg-[#0A0A0A]/95 p-7 shadow-[0_20px_60px_rgba(0,0,0,0.7)] backdrop-blur-xl">
          <h2 className="mb-6 font-heading text-xl font-semibold">{titles[mode]}</h2>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field icon={User} placeholder="Full name" value={form.name} onChange={set("name")} testid="auth-name" />
            )}
            {(mode === "signin" || mode === "register" || mode === "forgot") && (
              <Field icon={Mail} type="email" placeholder="Email address" value={form.email} onChange={set("email")} testid="auth-email" />
            )}
            {(mode === "signin" || mode === "register" || mode === "reset") && (
              <Field icon={Lock} type="password" placeholder={mode === "reset" ? "New password" : "Password"} value={form.password} onChange={set("password")} testid="auth-password" />
            )}

            {mode === "signin" && (
              <button type="button" onClick={() => setMode("forgot")} className="text-xs text-gold/70 hover:text-gold" data-testid="forgot-link">
                Forgot password?
              </button>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-gold-light to-gold py-3 text-sm font-semibold text-black shadow-gold-sm transition-all hover:shadow-gold disabled:opacity-60"
              data-testid="auth-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
                {mode === "signin" ? "Sign In" : mode === "register" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : "Update Password"}
                <ArrowRight className="h-4 w-4" />
              </>}
            </button>
          </form>

          {(mode === "signin" || mode === "register") && (
            <>
              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 gold-hairline" />
                <span className="text-[11px] uppercase tracking-wider text-white/30">or</span>
                <div className="h-px flex-1 gold-hairline" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {["Google", "Apple", "Microsoft"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => toast("Social sign-in is coming soon")}
                    className="rounded-xl border border-white/10 py-2.5 text-xs text-white/50 transition-colors hover:border-gold/25 hover:text-white/80"
                    data-testid={`social-${p.toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </>
          )}

          <p className="mt-6 text-center text-sm text-white/45">
            {mode === "signin" ? (
              <>Don't have an account?{" "}
                <button onClick={() => setMode("register")} className="font-medium text-gold hover:underline" data-testid="switch-register">Sign up</button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button onClick={() => setMode("signin")} className="font-medium text-gold hover:underline" data-testid="switch-signin">Sign in</button>
              </>
            )}
          </p>
        </div>
      </motion.div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-graphite px-4 py-3 transition-colors focus-within:border-gold/40">
      <Icon className="h-4 w-4 shrink-0 text-white/30" />
      <input
        {...props}
        required
        data-testid={testid}
        className="w-full bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
      />
    </div>
  );
}
